import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  BonusCancellationRow,
  BonusHoursRow,
  BonusMemberSection,
  BonusNoChargeRow,
  BonusSalesRow,
} from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { api, mutationError } from '../../api/client.js';
import {
  useBonusReport,
  useSendBonusReport,
  useSetBonusDisqualification,
  useSetTimesheetTiming,
} from './bonusApi.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function money(value: string): string {
  return currency.format(Number(value));
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${m}/${d}/${y}`;
}

const TH = 'whitespace-nowrap px-3 py-2 text-left font-medium';
const THR = 'whitespace-nowrap px-3 py-2 text-right font-medium';
const TD = 'whitespace-nowrap px-3 py-1.5 text-slate-600';
const TDR = 'whitespace-nowrap px-3 py-1.5 text-right text-slate-600';

function SectionTable({
  title,
  head,
  children,
  footLabel,
  footValue,
  span,
}: {
  title: string;
  head: React.ReactNode;
  children: React.ReactNode;
  footLabel: string;
  footValue: string;
  span: number;
}): JSX.Element {
  return (
    <div className="mt-3">
      <h4 className="mb-1 text-sm font-semibold text-slate-800">{title}</h4>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>{head}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">{children}</tbody>
          <tfoot className="border-t-2 border-slate-200 text-sm font-semibold text-slate-900">
            <tr>
              <td className="px-3 py-2 text-right" colSpan={span}>
                {footLabel}
              </td>
              <td className="px-3 py-2 text-right">{footValue}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function HoursSection({
  rows,
  total,
  editable,
  onSetTiming,
  onSetDisqualified,
}: {
  rows: BonusHoursRow[];
  total: string;
  editable: boolean;
  onSetTiming: (timesheetId: string, onTime: boolean) => void;
  onSetDisqualified: (timesheetId: string, disqualified: boolean, note: string | null) => void;
}): JSX.Element | null {
  if (rows.length === 0) return null;
  return (
    <SectionTable
      title="Hours bonus"
      span={9}
      footLabel="Hours bonus total"
      footValue={money(total)}
      head={
        <>
          <th className={TH}>Timesheet Week</th>
          <th className={TH}>Deadline</th>
          <th className={TH}>On Time</th>
          <th className={TH}>Disqualified</th>
          <th className={THR}>Billable Hrs</th>
          <th className={THR}>Threshold</th>
          <th className={THR}>Hrs Over</th>
          <th className={THR}>Bonus Potential</th>
          <th className={THR}>Adjusted Bonus</th>
        </>
      }
    >
      {rows.map((r) => (
        <tr key={r.timesheetId}>
          <td className={TD}>{fmtDate(r.weekStartDate)}</td>
          <td className={TD}>{fmtDate(r.dueAt)}</td>
          <td className={TD}>
            {editable ? (
              <span className="inline-flex overflow-hidden rounded-md border border-slate-300">
                <button
                  type="button"
                  onClick={() => onSetTiming(r.timesheetId, true)}
                  className={`px-2 py-0.5 text-xs ${r.onTime ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                >
                  On time
                </button>
                <button
                  type="button"
                  onClick={() => onSetTiming(r.timesheetId, false)}
                  className={`px-2 py-0.5 text-xs ${!r.onTime ? 'bg-rose-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                >
                  Late
                </button>
              </span>
            ) : r.onTime ? (
              'Yes'
            ) : (
              'No'
            )}
          </td>
          <td className={TD}>
            <DisqualifyCell row={r} editable={editable} onSet={onSetDisqualified} />
          </td>
          <td className={TDR}>{r.billableHours}</td>
          <td className={TDR}>{r.threshold}</td>
          <td className={TDR}>{r.hoursOverThreshold}</td>
          <td className={TDR}>{money(r.bonusPotential)}</td>
          <td className={`${TDR} font-medium text-slate-900`}>{money(r.adjustedBonus)}</td>
        </tr>
      ))}
    </SectionTable>
  );
}

function DisqualifyCell({
  row,
  editable,
  onSet,
}: {
  row: BonusHoursRow;
  editable: boolean;
  onSet: (timesheetId: string, disqualified: boolean, note: string | null) => void;
}): JSX.Element {
  const [note, setNote] = useState(row.disqualifiedNote ?? '');
  useEffect(() => {
    setNote(row.disqualifiedNote ?? '');
  }, [row.disqualifiedNote]);

  if (!editable) {
    if (!row.disqualified) return <span className="text-slate-400">No</span>;
    return (
      <span className="text-rose-700">
        Yes{row.disqualifiedNote ? ` — ${row.disqualifiedNote}` : ''}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2">
      <input
        type="checkbox"
        checked={row.disqualified}
        onChange={(e) =>
          onSet(row.timesheetId, e.target.checked, e.target.checked ? note.trim() || null : null)
        }
        className="h-4 w-4 rounded border-slate-300"
      />
      {row.disqualified && (
        <input
          type="text"
          value={note}
          placeholder="Reason"
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => onSet(row.timesheetId, true, note.trim() || null)}
          className="w-36 rounded border border-slate-300 px-1.5 py-0.5 text-xs"
        />
      )}
    </span>
  );
}

function SalesSection({ rows, total }: { rows: BonusSalesRow[]; total: string }): JSX.Element | null {
  if (rows.length === 0) return null;
  return (
    <SectionTable
      title="Product sales bonus"
      span={4}
      footLabel="Product sales bonus total"
      footValue={money(total)}
      head={
        <>
          <th className={TH}>Product</th>
          <th className={TH}>Company</th>
          <th className={TH}>Date Invoice</th>
          <th className={TH}>Invoice #</th>
          <th className={THR}>Bonus</th>
        </>
      }
    >
      {rows.map((r) => (
        <tr key={r.invoiceId}>
          <td className={TD}>{r.subtype}</td>
          <td className={TD}>{r.customerName}</td>
          <td className={TD}>{fmtDate(r.issueDate)}</td>
          <td className={TD}>{r.invoiceNumber}</td>
          <td className={`${TDR} font-medium text-slate-900`}>{money(r.bonus)}</td>
        </tr>
      ))}
    </SectionTable>
  );
}

function NoChargeSection({
  rows,
  total,
}: {
  rows: BonusNoChargeRow[];
  total: string;
}): JSX.Element | null {
  if (rows.length === 0) return null;
  return (
    <SectionTable
      title="Product no-charge hours reduction"
      span={7}
      footLabel="No-charge reduction total"
      footValue={`-${money(total)}`}
      head={
        <>
          <th className={TH}>Billing Class</th>
          <th className={TH}>Service Category</th>
          <th className={TH}>Company</th>
          <th className={TH}>Charge To</th>
          <th className={TH}>Member</th>
          <th className={TH}>Date</th>
          <th className={THR}>Hours</th>
          <th className={THR}>Reduction</th>
        </>
      }
    >
      {rows.map((r) => (
        <tr key={r.timeEntryId}>
          <td className={TD}>{r.billingClass}</td>
          <td className={TD}>{r.serviceCategoryName}</td>
          <td className={TD}>{r.customerName}</td>
          <td className={`${TD} max-w-xs truncate`}>{r.chargeTo}</td>
          <td className={TD}>{r.memberName}</td>
          <td className={TD}>{fmtDate(r.workDate)}</td>
          <td className={TDR}>{r.hours}</td>
          <td className={`${TDR} font-medium text-rose-700`}>-{money(r.reduction)}</td>
        </tr>
      ))}
    </SectionTable>
  );
}

function CancellationSection({
  rows,
  total,
}: {
  rows: BonusCancellationRow[];
  total: string;
}): JSX.Element | null {
  if (rows.length === 0) return null;
  return (
    <SectionTable
      title="Product cancellations reduction"
      span={6}
      footLabel="Cancellation reduction total"
      footValue={`-${money(total)}`}
      head={
        <>
          <th className={TH}>Agreement #</th>
          <th className={TH}>Agreement</th>
          <th className={TH}>Product</th>
          <th className={TH}>Company</th>
          <th className={TH}>End Date</th>
          <th className={THR}>Monthly Revenue</th>
          <th className={THR}>Reduction</th>
        </>
      }
    >
      {rows.map((r) => (
        <tr key={r.agreementLineId}>
          <td className={TD}>{r.agreementNumber}</td>
          <td className={TD}>{r.agreementName}</td>
          <td className={TD}>{r.subtype}</td>
          <td className={TD}>{r.customerName}</td>
          <td className={TD}>{fmtDate(r.endDate)}</td>
          <td className={TDR}>{money(r.monthlyRevenue)}</td>
          <td className={`${TDR} font-medium text-rose-700`}>-{money(r.reduction)}</td>
        </tr>
      ))}
    </SectionTable>
  );
}

function MemberBlock({
  member,
  editable,
  onSetTiming,
  onSetDisqualified,
}: {
  member: BonusMemberSection;
  editable: boolean;
  onSetTiming: (timesheetId: string, onTime: boolean) => void;
  onSetDisqualified: (timesheetId: string, disqualified: boolean, note: string | null) => void;
}): JSX.Element {
  return (
    <section
      className={`mb-6 rounded-xl border bg-white p-4 shadow-sm ${
        member.activePip ? 'border-rose-300' : 'border-slate-200'
      }`}
    >
      <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
        {member.memberName}
        {member.activePip ? (
          <span className="inline-block rounded bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-700">
            DISQUALIFIED — Active PIP
          </span>
        ) : null}
      </h2>
      <HoursSection
        rows={member.hoursRows}
        total={member.hoursBonusTotal}
        editable={editable}
        onSetTiming={onSetTiming}
        onSetDisqualified={onSetDisqualified}
      />
      <SalesSection rows={member.salesRows} total={member.salesBonusTotal} />
      <NoChargeSection rows={member.noChargeRows} total={member.noChargeReductionTotal} />
      <CancellationSection rows={member.cancellationRows} total={member.cancellationReductionTotal} />
      <div className="mt-3 border-t-2 border-slate-900 pt-2 text-right text-base font-bold text-slate-900">
        {member.memberName} grand total: {money(member.grandTotal)}
      </div>
      {member.activePip ? (
        <p className="mt-1 text-right text-xs font-semibold text-rose-700">
          Disqualified (Active PIP) — bonus not awarded and excluded from the report grand total.
        </p>
      ) : null}
    </section>
  );
}

export function BonusReportPage(): JSX.Element {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const query = useBonusReport();
  const sendMutation = useSendBonusReport();
  const setTimingMutation = useSetTimesheetTiming();
  const setDisqualifyMutation = useSetBonusDisqualification();
  const [recipients, setRecipients] = useState('');
  const [sendMode, setSendMode] = useState<'members' | 'recipients'>('members');
  const [message, setMessage] = useState<string | null>(null);

  const report = query.data;

  const handleSetTiming = (timesheetId: string, onTime: boolean): void => {
    setTimingMutation.mutate({ timesheetId, timing: onTime ? 'on_time' : 'past_due' });
  };

  const handleSetDisqualified = (
    timesheetId: string,
    disqualified: boolean,
    note: string | null,
  ): void => {
    setDisqualifyMutation.mutate({ timesheetId, disqualified, note });
  };

  const handleSend = (): void => {
    setMessage(null);
    const list = recipients
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean);
    if (sendMode === 'recipients' && list.length === 0) {
      setMessage('Enter at least one email address (comma separated).');
      return;
    }
    sendMutation.mutate(
      { recipients: list, mode: sendMode },
      {
        onSuccess: (res) =>
          setMessage(
            `Sent ${res.recipients} email(s) across ${res.memberCount} member(s).`,
          ),
        onError: (err) => setMessage(err instanceof Error ? err.message : 'Failed to send.'),
      },
    );
  };

  const handleDownloadPdf = async (): Promise<void> => {
    setMessage(null);
    try {
      const { blob, fileName } = await api.download('/reports/bonus.pdf');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? `bonus-report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      // Without this the request just fails silently and the button looks dead.
      setMessage(mutationError(error, 'Failed to download the PDF.'));
    }
  };

  return (
    <div>
      <PageHeader
        title="Monthly Team Bonus"
        description={
          report
            ? `${report.monthLabel} (${fmtDate(report.periodStart)} – ${fmtDate(report.periodEnd)})`
            : 'Bonus for the previous calendar month, across all active members.'
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate('/reports')}>
              Back to reports
            </Button>
            <Button variant="secondary" onClick={() => void handleDownloadPdf()}>
              Download PDF
            </Button>
          </>
        }
      />

      {isAdmin ? (
        <div className="mb-4 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4">
          <div>
            <span className="mb-1 block text-sm font-medium text-slate-700">Send as</span>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="bonus-send-mode"
                  value="members"
                  checked={sendMode === 'members'}
                  onChange={() => setSendMode('members')}
                />
                Email each member their own metrics
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name="bonus-send-mode"
                  value="recipients"
                  checked={sendMode === 'recipients'}
                  onChange={() => setSendMode('recipients')}
                />
                Send all members to the addresses below
              </label>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[240px]">
              <label className="mb-1 block text-sm font-medium text-slate-700">
                {sendMode === 'recipients'
                  ? 'Recipient addresses (comma-separated)'
                  : 'Recipient addresses (not used when emailing members directly)'}
              </label>
              <input
                type="text"
                value={recipients}
                onChange={(e) => setRecipients(e.target.value)}
                placeholder="name@nscatalyst.com, other@nscatalyst.com"
                disabled={sendMode === 'members'}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400"
              />
            </div>
            <Button onClick={handleSend} disabled={sendMutation.isPending}>
              {sendMutation.isPending ? 'Sending…' : 'Send'}
            </Button>
          </div>
          {message ? <p className="text-sm text-slate-600">{message}</p> : null}
        </div>
      ) : null}

      {report ? (
        <div className="mb-4 rounded-xl border-2 border-slate-900 bg-white px-4 py-3 text-right">
          <span className="text-lg font-extrabold text-slate-900">
            Report grand total: {money(report.grandTotal)}
          </span>
          {report.members.some((m) => m.activePip) ? (
            <p className="mt-1 text-xs font-semibold text-rose-700">
              Disqualified members (Active PIP) are shown below but excluded from the report grand
              total.
            </p>
          ) : null}
        </div>
      ) : null}

      <DataState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
        isEmpty={Boolean(report && report.members.length === 0)}
        emptyTitle="No bonus activity"
        emptyDescription="There is no bonus activity for the previous month."
      >
        {report?.members.map((m) => (
          <MemberBlock
            key={m.memberId}
            member={m}
            editable={isAdmin}
            onSetTiming={handleSetTiming}
            onSetDisqualified={handleSetDisqualified}
          />
        ))}
      </DataState>
    </div>
  );
}
